import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, ProductCard } from "@/components/common";
import { getBrand, productsByBrand, SITE } from "@/mock";

export default function BrandPage() {
  const { slug } = useParams();
  const brand = getBrand(slug);
  if (!brand) return <Navigate to="/mattresses" replace />;
  const items = productsByBrand(slug);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Mattresses", path: "/mattresses" },
    { name: brand.name, path: `/brands/${brand.slug}` },
  ];

  return (
    <>
      <Seo
        title={`${brand.name} Mattresses in Fort Lauderdale`}
        description={`${brand.description} Shop ${brand.name} at MattressTown in Fort Lauderdale with same-day delivery.`}
        path={`/brands/${brand.slug}`}
        image={brand.image}
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />

      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: brand.accent || "#111111" }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="inline-block rounded-full bg-[#D0021B] px-4 py-1 text-xs font-bold uppercase tracking-widest text-white">{brand.save}</span>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-bold uppercase text-white sm:text-6xl">{brand.name}</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/90">{brand.description}</p>
          <a href={SITE.phoneHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-100">
            Call for Best Price <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold uppercase text-neutral-900">Shop {brand.name}</h2>
        {items.length ? (
          <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            {items.map((p) => (<ProductCard key={p.slug} product={p} />))}
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-neutral-200 bg-cream p-8 text-center">
            <p className="text-lg font-semibold text-neutral-900">Now available at MattressTown.</p>
            <p className="mx-auto mt-2 max-w-xl text-neutral-600">
              Our full {brand.name} lineup is on display in our Fort Lauderdale showroom. Call or visit
              and our team will help you find the perfect {brand.name} mattress.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-7 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]">Call {SITE.phone}</a>
              <a href={SITE.address.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 px-7 py-3 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-900 hover:text-white">Visit Showroom</a>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
